import React, { useState, useEffect, useRef } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { MessageComponent } from '@syncfusion/ej2-react-notifications';
import { useSearchParams, useNavigate } from 'react-router-dom';
// import { useStore } from '../../store';
import { useTranslation } from 'react-i18next';
import { TenantSelector } from '../components/TenantSelector';
import { api } from '../api/api';
import { getSelectedTenant, setSelectedTenant, type Tenant } from '../config/environment';
import { useTenants } from '../hooks/useTenants';
import { useLogin } from '../hooks/useLogin';
import { fetchLegalLinks, type LegalLinks } from '../services/LegalLinksService';

interface LoginResponse {
  token: string;
  userUuid: string;
  username: string;
  tenantUuid: string;
  tenantName: string;
  role: string | null;
  superAdmin: boolean;
  permissions: string[] | null;
}

const meetsPasswordPolicy = (value: string) => {
  if (!value) return false;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasLength = value.length >= 10;
  return hasUpper && hasLower && hasNumber && hasLength;
};

// Syncfusion's change event can fire with value === undefined when a TextBox
// instance is torn down mid-render (observed on Firefox when React recovers
// from an interrupted concurrent render): the undefined value wiped the
// state and the login was submitted as {}. Track the value on every input
// event as well and never accept undefined.
const trackedSetter = (setter: (value: string) => void) => (e: any) => {
  const value = e?.value ?? e?.target?.value;
  if (typeof value === 'string') {
    setter(value);
  }
};

export default function LoginPage() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tenant, setTenant] = useState<Tenant | null>(getSelectedTenant());
  const [tenantError, setTenantError] = useState('');
  // First-login password change: set when the backend answers the login with
  // 401 + errorCode AUTH_006 (force_password_change flag on the account).
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  // Terms of use and privacy notice; only the demo server sets them.
  const [legalLinks, setLegalLinks] = useState<LegalLinks | null>(null);

  useEffect(() => {
    fetchLegalLinks()
      .then(setLegalLinks)
      .catch(() => setLegalLinks(null));
  }, []);

  // Stable handler references. Syncfusion re-binds an event whose prop identity changes
  // between renders, and in Firefox that re-binding crashed the login page (TypeError in
  // Observer.notExist during TextBoxComponent.updateProperties): the page went blank and the
  // E2E login test failed there. The useState setters are stable, so these never change.
  //
  // useRef, not useMemo: React documents useMemo as a performance optimization whose cache it
  // may discard, so it guarantees a stable identity only by today's behaviour. Here the identity
  // IS the correctness condition, and a ref carries that guarantee for the life of the component.
  const onUsernameInput = useRef(trackedSetter(setUsername)).current;
  const onPasswordInput = useRef(trackedSetter(setPassword)).current;
  const onNewPasswordInput = useRef(trackedSetter(setNewPassword)).current;
  const onConfirmNewPasswordInput = useRef(trackedSetter(setConfirmNewPassword)).current;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const login = useLogin();
  const { tenants, loading: tenantsLoading, error: tenantsError } = useTenants();

  useEffect(() => {
    // If only one tenant, auto-select it
    if (tenants.length === 1) {
      setTenant(tenants[0]);
      setSelectedTenant(tenants[0]);
    }
  }, [tenants]);

  useEffect(() => {
    // Capture redirect URL parameter for deep linking
    const redirectUrl = searchParams.get('redirect');
    if (redirectUrl) {
      // Store the redirect URL in session storage
      sessionStorage.setItem('redirectUrl', redirectUrl);
    }
  }, [searchParams]);

  // Check if we have a redirect URL for display purposes
  // const hasRedirectUrl = searchParams.get('redirect') !== null;

  const handleTenantSelect = (selected: Tenant) => {
    setTenant(selected);
    setTenantError('');
  };

  const performLogin = async (loginPassword: string) => {
    const response = await api.post<LoginResponse>('/auth/login', { username, password: loginPassword });
    if (!response.data) {
      throw new Error(t('auth.login_error'));
    }

    const loginData = {
      accessToken: response.data.token,
    };

    // Use the useLogin hook which handles redirect logic
    await login(loginData);
  };

  const handleLoginError = (err: any) => {
    if (err.response && err.response.status === 401) {
      if (err.response.data?.errorCode === 'AUTH_006') {
        // Credentials are valid but the account requires a password change
        // before a session is issued — switch the form to change mode.
        setPasswordChangeRequired(true);
        setError('');
        return;
      }
      setError(t('auth.invalid_credentials'));
    } else if (err.response && err.response.status === 403) {
      setError(t('auth.no_access'));
    } else if (err.message && err.message.includes('No permissions assigned')) {
      setError(t('auth.no_permissions'));
    } else if (err.message && (err.message.includes('Network Error') || err.message.includes('Failed to fetch'))) {
      setError(t('auth.backend_unavailable'));
    } else {
      setError(t('auth.login_error'));
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Always require tenant selection
    if (!tenant) {
      setTenantError(t('tenant.required'));
      return;
    }

    try {
      await performLogin(password);
    } catch (err: any) {
      handleLoginError(err);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!meetsPasswordPolicy(newPassword)) {
      setError(t('validation.password_policy'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError(t('validation.password_mismatch'));
      return;
    }
    if (newPassword === password) {
      setError(t('auth.password_must_differ'));
      return;
    }

    try {
      await api.post('/auth/change-password', {
        username,
        currentPassword: password,
        newPassword,
      });
      // Password changed — log in directly with the new password.
      setPasswordChangeRequired(false);
      setPassword(newPassword);
      setNewPassword('');
      setConfirmNewPassword('');
      setError('');
      await performLogin(newPassword);
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError(t('auth.invalid_credentials'));
      } else if (err.response && err.response.status === 400) {
        setError(t('auth.passwordChangeFailed'));
      } else if (err.message && (err.message.includes('Network Error') || err.message.includes('Failed to fetch'))) {
        setError(t('auth.backend_unavailable'));
      } else {
        handleLoginError(err);
      }
    }
  };

  const isLoginDisabled =
    !username || !password || !tenant || tenantsLoading;

  if (tenantsError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4">
        <div className="bg-white shadow-lg rounded-xl p-10 w-full max-w-sm space-y-6">
          <div className="text-center">
            <img src="PlanBack_r.png" alt="Logo" className="mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">{t('actions.login')}</h2>
          </div>
          <MessageComponent
            severity="Error"
            content={`Failed to load tenants: ${tenantsError}`}
            cssClass="mb-4"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4">
      <form
        onSubmit={passwordChangeRequired ? handlePasswordChange : handleLogin}
        className="bg-white shadow-lg rounded-xl p-10 w-full max-w-sm space-y-6"
      >
        <div className="text-center">
          <img src="PlanBack_r.png" alt="Logo" className="mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">{t('actions.login')}</h2>
        </div>

        {passwordChangeRequired && (
          <MessageComponent
            severity="Info"
            content={t('auth.password_change_required')}
            cssClass="mb-4"
          />
        )}

        {error && (
          <MessageComponent
            severity="Error"
            content={error}
            cssClass="mb-4"
          />
        )}

        {/* Tenant selection section */}
        <div className="space-y-2" data-testid="tenant-selector">
          <TenantSelector onTenantSelect={handleTenantSelect} />
          {tenantError && (
            <MessageComponent
              severity="Error"
              content={tenantError}
              cssClass="mb-4"
            />
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-600">{t('auth.username')}</label>
          <div data-testid="login-username">
            <TextBoxComponent
              floatLabelType="Never"
              cssClass="e-outline w-full"
              placeholder={t('auth.username_placeholder')}
              value={username}
              input={onUsernameInput}
              onChange={onUsernameInput}
              htmlAttributes={{ 'data-testid': 'login-username-input' }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-600">
            {passwordChangeRequired ? t('auth.currentPassword') : t('auth.password')}
          </label>
          <div data-testid="login-password">
            <TextBoxComponent
              type="password"
              floatLabelType="Never"
              cssClass="e-outline w-full"
              placeholder={t('auth.password_placeholder')}
              value={password}
              input={onPasswordInput}
              onChange={onPasswordInput}
              htmlAttributes={{ 'data-testid': 'login-password-input' }}
            />
          </div>
        </div>

        {passwordChangeRequired && (
          <>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-600">{t('auth.newPassword')}</label>
              <div data-testid="login-new-password">
                <TextBoxComponent
                  type="password"
                  floatLabelType="Never"
                  cssClass="e-outline w-full"
                  placeholder={t('auth.newPasswordPlaceholder')}
                  value={newPassword}
                  input={onNewPasswordInput}
                  onChange={onNewPasswordInput}
                  htmlAttributes={{ 'data-testid': 'login-new-password-input' }}
                />
              </div>
              <div className="text-xs text-gray-500">{t('auth.passwordRequirements')}</div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-600">{t('auth.confirmPassword')}</label>
              <div data-testid="login-confirm-password">
                <TextBoxComponent
                  type="password"
                  floatLabelType="Never"
                  cssClass="e-outline w-full"
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  value={confirmNewPassword}
                  input={onConfirmNewPasswordInput}
                  onChange={onConfirmNewPasswordInput}
                  htmlAttributes={{ 'data-testid': 'login-confirm-password-input' }}
                />
              </div>
            </div>
          </>
        )}

        <div data-testid="login-submit">
        <ButtonComponent
          cssClass="e-primary w-full"
          type="submit"
          disabled={isLoginDisabled || (passwordChangeRequired && (!newPassword || !confirmNewPassword))}
        >
          {tenantsLoading
            ? t('common.loading')
            : passwordChangeRequired
              ? t('auth.change_password')
              : t('actions.login')}
        </ButtonComponent>
        </div>

        <div className="text-center">
          <ButtonComponent
            cssClass="e-link"
            type="button"
            onClick={() => navigate('/forgot-password')}
          >
            {t('auth.forgotPassword')}
          </ButtonComponent>
        </div>

        {legalLinks?.termsUrl && (
          <div className="text-center text-xs text-gray-500 space-y-1" data-testid="login-legal-links">
            <p>{t('auth.legalNotice')}</p>
            <p>
              <a href={legalLinks.termsUrl} target="_blank" rel="noopener noreferrer" className="underline">
                {t('auth.termsOfUse')}
              </a>
              {legalLinks.privacyUrl && (
                <>
                  {' · '}
                  <a href={legalLinks.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">
                    {t('auth.privacyNotice')}
                  </a>
                </>
              )}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
